import React from 'react';
import Button from '../Button';
import classes from './ScheduleCard.module.css';

/**
 * ScheduleCard Component
 * Displays a single report schedule with controls
 */
const ScheduleCard = ({ schedule, onEdit, onDelete, onToggle }) => {
	const {
		scheduleId,
		scheduleName,
		frequency,
		watcherFilter,
		showSubcategories,
		dayOfWeek,
		dayOfMonth,
		isActive,
		lastRun,
		lastRunStatus,
		hour,
		minute
	} = schedule;

	// Format watcher list
	const watcherList = watcherFilter 
		? watcherFilter.split(',').map(w => w.trim()).join(', ')
		: 'All Watchers';

	// Format frequency display
	const getFrequencyDisplay = () => {
		// Default to 8:00 if hour/minute not provided (backwards compatibility)
		const displayHour = hour !== undefined ? hour : 8;
		const displayMinute = minute !== undefined ? minute : 0;
		const timeString = `${String(displayHour).padStart(2, '0')}:${String(displayMinute).padStart(2, '0')} UTC`;
		
		if (frequency === 'daily') {
			return `Daily at ${timeString}`;
		} else if (frequency === 'weekly') {
			const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
			const day = days[dayOfWeek] || 'Monday';
			return `Weekly on ${day} at ${timeString}`;
		} else if (frequency === 'monthly') {
			return `Monthly on day ${dayOfMonth} at ${timeString}`;
		}
		return frequency;
	};

	// Format last run display
	const getLastRunDisplay = () => {
		if (!lastRun) return 'Never';
		
		const date = new Date(lastRun);
		const options = { 
			year: 'numeric', 
			month: 'short', 
			day: 'numeric', 
			hour: '2-digit', 
			minute: '2-digit' 
		};
		return date.toLocaleDateString('en-US', options);
	};

	return (
		<div className={`${classes.scheduleCard} ${!isActive ? classes.inactive : ''}`}>
			<div className={classes.cardHeader}>
				<div className={classes.titleSection}>
					<h3 className={classes.scheduleName}>{scheduleName}</h3>
					<span className={`${classes.statusBadge} ${isActive ? classes.active : classes.inactive}`}>
						{isActive ? 'Active' : 'Inactive'}
					</span>
				</div>
				<div className={classes.actions}>
					<Button
						variant="secondary"
						size="small"
						onClick={() => onToggle(scheduleId)}
					>
						{isActive ? 'Deactivate' : 'Activate'}
					</Button>
					<Button
						variant="secondary"
						size="small"
						onClick={() => onEdit(schedule)}
					>
						Edit
					</Button>
					<Button
						variant="danger"
						size="small"
						onClick={() => onDelete(scheduleId, scheduleName)}
					>
						Delete
					</Button>
				</div>
			</div>

			<div className={classes.cardBody}>
				<div className={classes.infoRow}>
					<span className={classes.label}>Frequency:</span>
					<span className={classes.value}>{getFrequencyDisplay()}</span>
				</div>

				<div className={classes.infoRow}>
					<span className={classes.label}>Watchers:</span>
					<span className={classes.value}>{watcherList}</span>
				</div>

				<div className={classes.infoRow}>
					<span className={classes.label}>Subcategory Breakdown:</span>
					<span className={classes.value}>{showSubcategories ? 'Enabled' : 'Disabled'}</span>
				</div>

				<div className={classes.infoRow}>
					<span className={classes.label}>Last Run:</span>
					<span className={classes.value}>
						{getLastRunDisplay()}
						{lastRunStatus && (
							<span className={`${classes.runStatus} ${classes[lastRunStatus.toLowerCase()]}`}>
								{` (${lastRunStatus})`}
							</span>
						)}
					</span>
				</div>
			</div>
		</div>
	);
};

export default ScheduleCard;

